import { Link as AmplifyLink } from '@aws-amplify/ui-react';
import React from 'react';
import { AiOutlineCamera } from 'react-icons/ai';
import General from '../utils/GeneralUtils';
import './UrlAttribution.scss';

interface UrlAttributionProps {
  url?: string;
  status?: string;
  arnScreenshot?: string;
  screenshotCreatedAt?: string; // Timestamp when the screenshot was taken
  prefix?: string; // e.g., "staff directory", "the staff directory"
  maxUrlLength?: number;
  showUrlOnSeparateLine?: boolean; // For URL_OTHER case where we say "the staff directory on [URL]"
  onSelectScreenshot?: (screenshot: { screenshot: string; screenshot_size: null; boxes: [] }) => void;
}

export const UrlAttribution: React.FC<UrlAttributionProps> = ({
  url,
  status,
  arnScreenshot,
  screenshotCreatedAt,
  prefix = 'staff directory',
  maxUrlLength = 80,
  showUrlOnSeparateLine = false,
  onSelectScreenshot
}) => {
  if (!url) return null;

  const isUnreachable = status === 'UNREACHABLE';

  // Format screenshot date as "Month Year" (e.g., "January 2024")
  let screenshotDateText = '';
  if (screenshotCreatedAt) {
    const date = new Date(screenshotCreatedAt);
    const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
    screenshotDateText = ` from ${date.toLocaleDateString('en-US', options)}`;
  }

  const baseMessage = arnScreenshot
    ? `<strong>This URL is no longer active.</strong><br />The page may have been moved or removed by the website owner.<br />A screenshot of the page${screenshotDateText} is available.`
    : "<strong>This URL is no longer active.</strong><br />The page may have been moved or removed by the website owner.";

  const tooltipHtml = `${baseMessage}<br/><br/>${General.ellipsisInMiddle(url, 80)}`;

  return (
    <>
      {isUnreachable ? (
        <span
          className="url-attribution__unreachable"
          data-tooltip-id="main-tooltip"
          data-tooltip-html={tooltipHtml}
        >
          {prefix}
        </span>
      ) : showUrlOnSeparateLine ? (
        <>
          {prefix} on <AmplifyLink target='_blank' href={url}>
            {General.ellipsisInMiddle(url, maxUrlLength)}
          </AmplifyLink>
        </>
      ) : (
        <>
          {prefix} <AmplifyLink target='_blank' href={url}>
            {General.ellipsisInMiddle(url, maxUrlLength)}
          </AmplifyLink>
        </>
      )}
      {arnScreenshot && onSelectScreenshot && (
        <span
          className='clickable ignore-click screenshot-icon url-attribution__screenshot-icon'
          onClick={() => {
            const screenshotKey = arnScreenshot.split('arn:aws:s3:::edulead-crawler/url/')[1];
            onSelectScreenshot({
              screenshot: screenshotKey,
              screenshot_size: null,
              boxes: []
            });
          }}
        >
          <AiOutlineCamera className="url-attribution__camera-icon" />
        </span>
      )}
    </>
  );
};
